import { Draw, HourglassEmpty, Visibility } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, Stack, Tooltip, Typography } from '@mui/material';
import type { ReactNode } from 'react';

export type AgreementStatus = 'signed' | 'ready' | 'unavailable';

interface AgreementSectionProps {
    title: string;
    description: string;
    status: AgreementStatus;
    alertMessage: string;
    actionLabel: string;
    actionDisabled?: boolean;
    /** Shown while the agreement document is loading or being submitted. */
    actionLoading?: boolean;
    /** Explains why the button is disabled (e.g. retainer not ready). */
    disabledReason?: string;
    onAction: () => void;
    showDivider?: boolean;
    /** Optional content under this retainer (e.g. visa fee table). */
    children?: ReactNode;
}

const alertSeverity = (status: AgreementStatus): 'success' | 'info' | 'warning' => {
    if (status === 'signed') return 'success';
    if (status === 'unavailable') return 'warning';
    return 'info';
};

const actionUi = (status: AgreementStatus, loading: boolean) => {
    if (loading) {
        return {
            variant: 'contained' as const,
            color: 'primary' as const,
            icon: <CircularProgress size={16} color="inherit" />,
        };
    }
    if (status === 'signed') {
        return {
            variant: 'outlined' as const,
            color: 'inherit' as const,
            icon: <Visibility fontSize="small" />,
        };
    }
    if (status === 'unavailable') {
        return {
            variant: 'outlined' as const,
            color: 'inherit' as const,
            icon: <HourglassEmpty fontSize="small" />,
        };
    }
    // ready — primary CTA to sign
    return {
        variant: 'contained' as const,
        color: 'primary' as const,
        icon: <Draw fontSize="small" />,
    };
};

/**
 * One agreement block: title + action on the same row, alert + details below.
 */
const AgreementSection = ({
    title,
    description,
    status,
    alertMessage,
    actionLabel,
    actionDisabled = false,
    actionLoading = false,
    disabledReason,
    onAction,
    showDivider = true,
    children,
}: AgreementSectionProps) => {
    const ui = actionUi(status, actionLoading);
    const disabled = actionDisabled || actionLoading;

    const button = (
        <Button
            variant={ui.variant}
            color={ui.color}
            disabled={disabled}
            onClick={onAction}
            startIcon={ui.icon}
            sx={{
                flexShrink: 0,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                letterSpacing: '-0.01em',
                minWidth: { xs: '100%', sm: 168 },
                px: 2,
                py: 1.05,
                borderRadius: 2,
                alignSelf: { xs: 'stretch', sm: 'center' },
                boxShadow: 'none',
                whiteSpace: 'nowrap',
                transition:
                    'background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease',
                ...(status === 'ready' &&
                    !actionLoading && {
                        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
                        '&:hover': {
                            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.28)',
                        },
                    }),
                ...(status === 'signed' && {
                    borderColor: 'divider',
                    color: 'text.primary',
                    bgcolor: 'background.paper',
                    '&:hover': {
                        borderColor: 'text.secondary',
                        bgcolor: 'action.hover',
                    },
                }),
                ...(status === 'unavailable' && {
                    borderColor: 'divider',
                    color: 'text.secondary',
                    bgcolor: 'action.hover',
                }),
            }}
        >
            {actionLoading ? 'Opening…' : actionLabel}
        </Button>
    );

    return (
        <Box
            sx={{
                px: { xs: 2, sm: 3 },
                py: { xs: 2.25, sm: 2.75 },
                borderBottom: showDivider ? '1px solid' : 'none',
                borderColor: 'divider',
            }}
        >
            {/* Title opposite action */}
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                sx={{
                    alignItems: { xs: 'stretch', sm: 'center' },
                    justifyContent: 'space-between',
                    mb: 1.5,
                }}
            >
                <Box sx={{ minWidth: 0, flex: 1, pr: { sm: 2 } }}>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 600,
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.35,
                        }}
                    >
                        {title}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5, lineHeight: 1.5 }}
                    >
                        {description}
                    </Typography>
                </Box>

                {disabled && disabledReason ? (
                    <Tooltip title={disabledReason} arrow placement="top">
                        {/* span needed so tooltip works on disabled button */}
                        <Box
                            component="span"
                            sx={{
                                display: 'inline-flex',
                                width: { xs: '100%', sm: 'auto' },
                            }}
                        >
                            {button}
                        </Box>
                    </Tooltip>
                ) : (
                    button
                )}
            </Stack>

            <Alert
                severity={alertSeverity(status)}
                sx={{
                    // alignItems: 'center',
                    '& .MuiAlert-message': { py: 0.5 },
                }}
            >
                {alertMessage}
            </Alert>

            {children && <Box sx={{ mt: 2 }}>{children}</Box>}
        </Box>
    );
};

export default AgreementSection;
